import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    justNow: string;
    halfMinute: string;
    halfMinuteShort: string;
    timeAgo: ({ unit, count }: {
        unit: string;
        count: number;
    }) => string;
    timeFromNow: ({ unit, count }: {
        unit: string;
        count: number;
    }) => string;
    timeShort: ({ unit, count }: {
        unit: string;
        count: number;
    }) => string;
    aboutTimeShort: ({ unit, count }: {
        unit: string;
        count: number;
    }) => string;
    lessThanTimeShort: ({ unit, count }: {
        unit: string;
        count: number;
    }) => string;
    overYearsShort: (count: number) => string;
    almostYearsShort: (count: number) => string;
    timeFull: ({ unit, count }: {
        unit: string;
        count: number;
    }) => string;
    withSuffixPast: ({ time }: {
        time: string;
    }) => string;
    withSuffixFuture: ({ time }: {
        time: string;
    }) => string;
};
export type Deps = {};
export default {
    DEFINE(_messages: Text, {}: Deps = {}): void {
        return Object.defineProperties(this, {
            /** Shown for very recent times (less than a minute or configured threshold) */
            justNow: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "useFormattedRelativeDate.justNow?c=28ug4npxxaslk", defaultMessage: "Just now" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Half a minute */
            halfMinute: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "useFormattedRelativeDate.halfMinute?c=7211oj34hc0a", defaultMessage: "half a minute" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Half a minute - truncated */
            halfMinuteShort: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "useFormattedRelativeDate.halfMinuteShort?c=1fok1az2l0s5x", defaultMessage: "half a m" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Relative time in the past (e.g., "2 hours ago", "1 day ago") */
            timeAgo: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ unit, count }: {
                        unit: string;
                        count: number;
                    }): string => __intl__.intl().formatMessage({ id: "useFormattedRelativeDate.timeAgo?c=36xgyva8ocwag", defaultMessage: "{unit, select, second {{count, plural, =0 {Just now} one {1 second ago} other {{count} seconds ago}}} minute {{count, plural, one {1 minute ago} other {{count} minutes ago}}} hour {{count, plural, one {1 hour ago} other {{count} hours ago}}} day {{count, plural, one {1 day ago} other {{count} days ago}}} week {{count, plural, one {1 week ago} other {{count} weeks ago}}} month {{count, plural, one {1 month ago} other {{count} months ago}}} year {{count, plural, one {1 year ago} other {{count} years ago}}} other {}}" }, { unit: unit, count: count }));
                },
                enumerable: true,
                configurable: true
            },
            /** Relative time in the future (e.g., "in 2 hours", "in 1 day") */
            timeFromNow: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ unit, count }: {
                        unit: string;
                        count: number;
                    }): string => __intl__.intl().formatMessage({ id: "useFormattedRelativeDate.timeFromNow?c=1lfjmnrytsatc", defaultMessage: "{unit, select, second {{count, plural, one {in 1 second} other {in {count} seconds}}} minute {{count, plural, one {in 1 minute} other {in {count} minutes}}} hour {{count, plural, one {in 1 hour} other {in {count} hours}}} day {{count, plural, one {in 1 day} other {in {count} days}}} week {{count, plural, one {in 1 week} other {in {count} weeks}}} month {{count, plural, one {in 1 month} other {in {count} months}}} year {{count, plural, one {in 1 year} other {in {count} years}}} other {}}" }, { unit: unit, count: count }));
                },
                enumerable: true,
                configurable: true
            },
            /** Truncated relative time (e.g., "2h", "1d", "3mo") */
            timeShort: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ unit, count }: {
                        unit: string;
                        count: number;
                    }): string => __intl__.intl().formatMessage({ id: "useFormattedRelativeDate.timeShort?c=bbzm10hy8psw", defaultMessage: "{unit, select, second {{count}s} minute {{count}m} hour {{count}h} day {{count}d} week {{count}w} month {{count}mo} year {{count}y} other {}}" }, { unit: unit, count: count }));
                },
                enumerable: true,
                configurable: true
            },
            /** Approximate truncated time (e.g., "about 2h", "about 1w") */
            aboutTimeShort: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ unit, count }: {
                        unit: string;
                        count: number;
                    }): string => __intl__.intl().formatMessage({ id: "useFormattedRelativeDate.aboutTimeShort?c=2ptx131yrpyc4", defaultMessage: "{unit, select, hour {{count, plural, one {about 1h} other {about {count}h}}} week {{count, plural, one {about 1w} other {about {count}w}}} month {{count, plural, one {about 1mo} other {about {count}mo}}} year {{count, plural, one {about 1y} other {about {count}y}}} other {}}" }, { unit: unit, count: count }));
                },
                enumerable: true,
                configurable: true
            },
            /** Less than specified time - truncated (e.g., "less than 2m") */
            lessThanTimeShort: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ unit, count }: {
                        unit: string;
                        count: number;
                    }): string => __intl__.intl().formatMessage({ id: "useFormattedRelativeDate.lessThanTimeShort?c=3fntczccs441q", defaultMessage: "{unit, select, minute {{count, plural, one {less than a m} other {less than {count}m}}} other {}}" }, { unit: unit, count: count }));
                },
                enumerable: true,
                configurable: true
            },
            /** Over specified years - truncated (e.g., "over 1y", "over 2y") */
            overYearsShort: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (count: number): string => __intl__.intl().formatMessage({ id: "useFormattedRelativeDate.overYearsShort?c=1iiwr6bgp4425", defaultMessage: "{count, plural, one {over 1y} other {over {count}y}}" }, { count: count }));
                },
                enumerable: true,
                configurable: true
            },
            /** Almost specified years - truncated (e.g., "almost 1y", "almost 2y") */
            almostYearsShort: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (count: number): string => __intl__.intl().formatMessage({ id: "useFormattedRelativeDate.almostYearsShort?c=hwcvmymn1gt3", defaultMessage: "{count, plural, one {almost 1y} other {almost {count}y}}" }, { count: count }));
                },
                enumerable: true,
                configurable: true
            },
            /** Full relative time without suffix (e.g., "2 days", "3 hours") */
            timeFull: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ unit, count }: {
                        unit: string;
                        count: number;
                    }): string => __intl__.intl().formatMessage({ id: "useFormattedRelativeDate.timeFull?c=1c28ggx9y9lk8", defaultMessage: "{unit, select, second {{count, plural, one {1 second} other {{count} seconds}}} minute {{count, plural, one {1 minute} other {{count} minutes}}} hour {{count, plural, one {1 hour} other {{count} hours}}} day {{count, plural, one {1 day} other {{count} days}}} week {{count, plural, one {1 week} other {{count} weeks}}} month {{count, plural, one {1 month} other {{count} months}}} year {{count, plural, one {1 year} other {{count} years}}} other {}}" }, { unit: unit, count: count }));
                },
                enumerable: true,
                configurable: true
            },
            /** Past time with suffix. Reorder {time} placeholder for language word order. */
            withSuffixPast: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ time }: {
                        time: string;
                    }): string => __intl__.intl().formatMessage({ id: "useFormattedRelativeDate.withSuffixPast?c=1h7hnyy4rgcvz", defaultMessage: "{time} ago" }, { time: time }));
                },
                enumerable: true,
                configurable: true
            },
            /** Future time with suffix. Reorder {time} placeholder for language word order. */
            withSuffixFuture: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ time }: {
                        time: string;
                    }): string => __intl__.intl().formatMessage({ id: "useFormattedRelativeDate.withSuffixFuture?c=ttq5g6sesp73", defaultMessage: "{time} from now" }, { time: time }));
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps?: Deps): void;
};
